import { useEffect, useState, type ReactNode } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useToast } from '../../components/ui/Toast';
import { TableRowSkeleton } from '../../components/ui/Skeleton';
import { EmptyState } from '../../components/ui/EmptyState';
import { useCampaign, useUpdateCampaign } from '../../api/campaigns.api';
import { useAudiences, useCreateAudience, type Audience } from '../../api/audiences.api';
import {
  FormCard,
  FormHeader,
  WizardLayout,
  WizardFooter,
  stepInfo,
  stepPath,
} from './builder-shell';

/* ── Screen 14 — Campaign builder · Audience step ── */

// The Audience step only ever shows for campaigns enrolling by audience.
const STEP_INFO = stepInfo('audience', 'audience');

export function CampaignAudiencePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();

  const { data: campaign, isLoading: loadingCampaign } = useCampaign(id);
  const { data: audiencesResp, isLoading: loadingAudiences } = useAudiences();
  const updateCampaign = useUpdateCampaign(id ?? '');
  const createAudience = useCreateAudience();

  const [selectedId, setSelectedId] = useState<string>('');
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState<'candidates' | 'contacts' | 'marketing'>('candidates');

  useEffect(() => {
    if (campaign?.audience_id) setSelectedId(campaign.audience_id);
  }, [campaign?.audience_id]);

  // NEWSLETTER campaigns are channel-isolated (Section 14): they MUST target a
  // marketing audience. Outreach campaigns are the inverse. Seed `newType` to
  // the correct default once campaign loads so users aren't fighting the form.
  useEffect(() => {
    if (campaign?.type === 'NEWSLETTER') setNewType('marketing');
    else if (campaign && newType === 'marketing') setNewType('candidates');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign?.type]);

  if (!id) return <Navigate to="/campaigns/new" replace />;
  if (loadingCampaign) return <LoadingShell />;
  if (!campaign) return <Navigate to="/campaigns" replace />;
  if (campaign.status !== 'DRAFT') return <Navigate to={`/campaigns/${id}`} replace />;

  // Filter to audiences the campaign type can actually use. NEWSLETTER →
  // marketing only; everything else → candidate/contact only. Prevents the
  // user from picking a mismatched audience and being rejected at launch.
  const allAudiences = audiencesResp?.data ?? [];
  const isNewsletter = campaign.type === 'NEWSLETTER';
  const audiences = allAudiences.filter((a) =>
    isNewsletter ? a.type === 'marketing' : a.type !== 'marketing'
  );
  const isPending = updateCampaign.isPending || createAudience.isPending;

  const onSelect = (audienceId: string) => {
    setSelectedId(audienceId);
    setShowCreate(false);
  };

  const onCreate = async () => {
    if (!newName.trim()) return;
    try {
      const created = await createAudience.mutateAsync({
        name: newName.trim(),
        type: newType,
        rules: [],
        is_dynamic: false,
      });
      addToast({ variant: 'success', title: 'Audience created' });
      setSelectedId(created.id);
      setShowCreate(false);
      setNewName('');
    } catch {
      addToast({ variant: 'danger', title: 'Could not create audience' });
    }
  };

  const persistAndNext = async () => {
    if (!selectedId) return;
    try {
      await updateCampaign.mutateAsync({ audience_id: selectedId });
      navigate(stepPath(id, 'sequence'));
    } catch {
      addToast({ variant: 'danger', title: 'Could not save audience' });
    }
  };

  return (
    <WizardLayout step="audience" campaignId={id} campaign={campaign}>
      <FormCard>
        <FormHeader stepIndex={STEP_INFO.index} totalSteps={STEP_INFO.total} label="Audience" />

        <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-md)', color: 'var(--color-text-secondary)', lineHeight: 'var(--leading-relaxed)' }}>
          {isNewsletter
            ? 'Newsletter campaigns send only to opted-in marketing contacts. Pick an existing marketing audience or create a new one.'
            : 'Pick an existing audience or list, or create a new one. Dynamic audiences refresh against JT data so new matches join automatically.'}
        </p>

        {isNewsletter && (
          <div
            style={{
              padding: 'var(--space-3) var(--space-4)',
              borderRadius: 'var(--radius-input)',
              backgroundColor: 'var(--color-surface-muted)',
              border: '1px solid var(--color-border-default)',
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-xs)',
              color: 'var(--color-text-muted)',
              lineHeight: 'var(--leading-relaxed)',
            }}
          >
            Newsletter and outreach are kept strictly separate. An unsubscribe here never affects direct
            recruiter outreach, and a candidate appearing in your CRM doesn't automatically join your newsletter.
            Add people to your marketing list in <Link to="/settings?section=marketing" style={{ color: 'var(--color-text-link)' }}>Settings, Marketing list</Link>,
            or from a candidate or contact in JobTetris.
          </div>
        )}

        {loadingAudiences ? (
          <TableRowSkeleton rows={4} />
        ) : audiences.length === 0 && !showCreate ? (
          <EmptyState
            icon={<IconUsers size={18} />}
            title="No audiences yet"
            description="Create your first audience to choose who this campaign reaches."
            action={<PrimaryActionButton onClick={() => setShowCreate(true)}>Create audience</PrimaryActionButton>}
          />
        ) : (
          <>
            <AudienceList audiences={audiences} selectedId={selectedId} onSelect={onSelect} />
            <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
              {!showCreate ? (
                <SecondaryActionButton onClick={() => setShowCreate(true)}>
                  <IconPlus size={14} />
                  <span style={{ marginLeft: 'var(--space-1)' }}>Create new audience</span>
                </SecondaryActionButton>
              ) : (
                <CreateAudienceInline
                  name={newName}
                  onName={setNewName}
                  type={newType}
                  onType={setNewType}
                  onCancel={() => {
                    setShowCreate(false);
                    setNewName('');
                  }}
                  onCreate={onCreate}
                  isPending={createAudience.isPending}
                />
              )}
            </div>
          </>
        )}

        <WizardFooter
          onBack={() => navigate(stepPath(id, 'triggers'))}
          onNext={persistAndNext}
          nextLabel="Next: Sequence"
          isPending={isPending}
          primaryDisabled={!selectedId}
          helper={
            <Link to="/audiences" style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-link)', textDecoration: 'none' }}>
              Manage all audiences →
            </Link>
          }
        />
      </FormCard>
    </WizardLayout>
  );
}

/* ── Audience list (radio cards) ── */

function AudienceList({
  audiences,
  selectedId,
  onSelect,
}: {
  audiences: Audience[];
  selectedId: string;
  onSelect: (id: string) => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
      {audiences.map((a) => {
        const isSelected = a.id === selectedId;
        return (
          <button
            key={a.id}
            type="button"
            onClick={() => onSelect(a.id)}
            style={{
              textAlign: 'left',
              padding: 'var(--space-4)',
              borderRadius: 'var(--radius-input)',
              border: `1.5px solid ${isSelected ? 'var(--color-action-primary)' : 'var(--color-border-default)'}`,
              backgroundColor: isSelected ? 'var(--color-blue-50)' : 'var(--color-surface-white)',
              boxShadow: isSelected ? 'var(--shadow-focus)' : 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
              fontFamily: 'var(--font-sans)',
              transition: 'border-color var(--transition-base), background-color var(--transition-base)',
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: '18px',
                height: '18px',
                borderRadius: 'var(--radius-full)',
                border: `2px solid ${isSelected ? 'var(--color-action-primary)' : 'var(--color-border-strong)'}`,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {isSelected && (
                <span
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: 'var(--radius-full)',
                    backgroundColor: 'var(--color-action-primary)',
                  }}
                />
              )}
            </span>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
              <span style={{ fontSize: 'var(--text-md)', fontWeight: 'var(--font-weight-semibold)', color: isSelected ? 'var(--color-text-brand)' : 'var(--color-text-primary)' }}>
                {a.name}
              </span>
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                {a.member_count.toLocaleString()} {a.member_count === 1 ? 'member' : 'members'} ·{' '}
                {a.is_dynamic ? 'Dynamic' : 'Static'} · Source: {a.type}
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
}

/* ── Inline create audience ── */

function CreateAudienceInline({
  name,
  onName,
  type,
  onType,
  onCancel,
  onCreate,
  isPending,
}: {
  name: string;
  onName: (v: string) => void;
  type: 'candidates' | 'contacts' | 'marketing';
  onType: (v: 'candidates' | 'contacts' | 'marketing') => void;
  onCancel: () => void;
  onCreate: () => void;
  isPending: boolean;
}) {
  // Marketing audiences are only relevant to NEWSLETTER campaigns. When the
  // type is locked to marketing (parent set it that way), we hide the picker
  // entirely so the user can't pick a mismatched source for the campaign.
  const isMarketingLocked = type === 'marketing';
  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-3)',
        padding: 'var(--space-4)',
        borderRadius: 'var(--radius-input)',
        border: '1px solid var(--color-border-default)',
        backgroundColor: 'var(--color-surface-muted)',
      }}
    >
      <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
        <input
          type="text"
          placeholder="Audience name (e.g. Backend Sydney)"
          value={name}
          onChange={(e) => onName(e.target.value)}
          style={{
            flex: 1,
            height: 'var(--input-height)',
            padding: '0 var(--space-3)',
            border: '1px solid var(--input-border)',
            borderRadius: 'var(--input-radius)',
            backgroundColor: 'var(--input-bg)',
            fontSize: 'var(--input-font-size)',
            fontFamily: 'var(--font-sans)',
            color: 'var(--color-text-primary)',
            outline: 'none',
          }}
        />
        {isMarketingLocked ? (
          // Visual confirmation that the type is locked to marketing — keeps
          // parity with the candidates/contacts dropdown without giving the
          // user a control that would just be rejected at launch.
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              height: 'var(--input-height)',
              padding: '0 var(--space-3)',
              borderRadius: 'var(--input-radius)',
              backgroundColor: 'var(--color-surface-muted)',
              border: '1px solid var(--input-border)',
              fontSize: 'var(--input-font-size)',
              fontFamily: 'var(--font-sans)',
              color: 'var(--color-text-secondary)',
            }}
          >
            Marketing contacts
          </span>
        ) : (
          <select
            value={type}
            onChange={(e) => onType(e.target.value as typeof type)}
            aria-label="Audience source type"
            style={{
              height: 'var(--input-height)',
              padding: '0 var(--space-3)',
              border: '1px solid var(--input-border)',
              borderRadius: 'var(--input-radius)',
              backgroundColor: 'var(--input-bg)',
              fontSize: 'var(--input-font-size)',
              fontFamily: 'var(--font-sans)',
              color: 'var(--color-text-primary)',
              outline: 'none',
              cursor: 'pointer',
            }}
          >
            <option value="candidates">Candidates</option>
            <option value="contacts">Contacts</option>
            {/* Applicants source type deferred to post-MVP pending JT backend work. */}
          </select>
        )}
      </div>
      <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
        You can add filters and rules after the campaign is created from the Audiences page.
      </p>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
        <SecondaryActionButton onClick={onCancel} disabled={isPending}>
          Cancel
        </SecondaryActionButton>
        <PrimaryActionButton onClick={onCreate} disabled={!name.trim() || isPending}>
          {isPending ? 'Creating…' : 'Create audience'}
        </PrimaryActionButton>
      </div>
    </div>
  );
}

function LoadingShell() {
  return (
    <WizardLayout step="audience">
      <FormCard>
        <p style={{ fontFamily: 'var(--font-sans)', color: 'var(--color-text-muted)' }}>Loading campaign…</p>
      </FormCard>
    </WizardLayout>
  );
}

/* ── Buttons ── */

function PrimaryActionButton({ children, onClick, disabled }: { children: ReactNode; onClick?: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 'var(--btn-height-sm)',
        padding: '0 var(--btn-padding-x-sm)',
        borderRadius: 'var(--btn-radius)',
        backgroundColor: 'var(--color-action-primary)',
        color: 'var(--color-action-primary-text)',
        border: 'none',
        fontSize: 'var(--text-xs)',
        fontWeight: 'var(--font-weight-semibold)',
        fontFamily: 'var(--font-sans)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {children}
    </button>
  );
}

function SecondaryActionButton({ children, onClick, disabled }: { children: ReactNode; onClick?: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 'var(--btn-height-sm)',
        padding: '0 var(--btn-padding-x-sm)',
        borderRadius: 'var(--btn-radius)',
        backgroundColor: 'var(--color-action-secondary)',
        color: 'var(--color-action-secondary-text)',
        border: '1px solid var(--color-action-secondary-border)',
        fontSize: 'var(--text-xs)',
        fontWeight: 'var(--font-weight-semibold)',
        fontFamily: 'var(--font-sans)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {children}
    </button>
  );
}

/* ── Icons ── */

function IconUsers({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function IconPlus({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}
